// Batch Traceability — visual graph for /api/stock/trace/{rm_id}. Two side-
// by-side lanes: Backward (Supplier → GRN → Batch → Lot) and Forward
// (Lot → Work Order / Sales Order). Lets QA follow a defect end-to-end.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function StockTraceability() {
  const [rms, setRms] = useState([]);
  const [rmId, setRmId] = useState("");
  const [trace, setTrace] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/raw-materials/").then(r => setRms(r.data)); }, []);
  const load = async () => {
    if (!rmId) return;
    setBusy(true); setTrace(null);
    try { const { data } = await api.get(`/stock/trace/${rmId}`); setTrace(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Trace failed"); }
    setBusy(false);
  };

  const inp = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
  return (
    <div className="space-y-4" data-testid="trace-page">
      <div className="overline text-[10px] text-[var(--accent)]">QUALITY</div>
      <h1 className="text-2xl font-semibold">Batch Traceability</h1>
      <p className="text-sm text-zinc-400">Backward: Supplier → GRN → Batch → Lot. Forward: Lot → Work Order / Sales Order.</p>
      <div className="tactical-card p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1 flex-1 max-w-md">
          <div>Raw Material</div>
          <select data-testid="trace-rm" value={rmId} onChange={e => setRmId(e.target.value)} className={inp}>
            <option value="">— Select —</option>
            {rms.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
          </select>
        </label>
        <button data-testid="trace-run" onClick={load} disabled={!rmId || busy}
          className="h-10 px-6 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm disabled:opacity-50">
          {busy ? "Tracing…" : "Trace"}
        </button>
      </div>
      {trace && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="tactical-card overflow-x-auto">
            <div className="p-4 border-b border-[var(--border)]">
              <div className="text-sm font-semibold">← Backward Trace</div>
              <div className="text-xs text-zinc-400">Supplier · GRN · Batch · Lot ({trace.backward.length})</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                  <th className="text-left px-4 py-2">Supplier</th>
                  <th className="text-left px-4 py-2">GRN / Invoice</th>
                  <th className="text-left px-4 py-2">Batch</th>
                  <th className="text-right px-4 py-2">Received</th>
                  <th className="text-left px-4 py-2">QC</th>
                </tr>
              </thead>
              <tbody>
                {trace.backward.length === 0 && <tr><td colSpan="5" className="text-center px-4 py-6 text-zinc-500">No inbound lots</td></tr>}
                {trace.backward.map((r, i) => (
                  <tr key={i} className="border-b border-[var(--border)] last:border-b-0" data-testid={`trace-back-${i}`}>
                    <td className="px-4 py-2">{r.supplier_name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px]">{r.invoice_no || r.grn_id?.slice(0,8) || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px]">{r.batch_no}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.qty_received} <span className="text-zinc-500 text-[10px]">used {r.qty_used}</span></td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase ${
                        r.quality_status === "Passed" ? "bg-emerald-500/15 text-emerald-300" :
                        r.quality_status === "Rejected" ? "bg-red-500/15 text-red-300" :
                        r.quality_status === "Hold" ? "bg-amber-500/15 text-amber-300" :
                        "bg-zinc-500/15 text-zinc-400"
                      }`}>{r.quality_status || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="tactical-card overflow-x-auto">
            <div className="p-4 border-b border-[var(--border)]">
              <div className="text-sm font-semibold">→ Forward Trace</div>
              <div className="text-xs text-zinc-400">Consumed by Work / Sales Orders ({trace.forward.length})</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                  <th className="text-left px-4 py-2">Order</th>
                  <th className="text-right px-4 py-2">Consumed</th>
                  <th className="text-left px-4 py-2">First → Last</th>
                  <th className="text-left px-4 py-2">Types</th>
                </tr>
              </thead>
              <tbody>
                {trace.forward.length === 0 && <tr><td colSpan="4" className="text-center px-4 py-6 text-zinc-500">Not consumed yet</td></tr>}
                {trace.forward.map((r, i) => (
                  <tr key={i} className="border-b border-[var(--border)] last:border-b-0" data-testid={`trace-fwd-${i}`}>
                    <td className="px-4 py-2 font-mono text-[11px]">{r.order_id}</td>
                    <td className="px-4 py-2 text-right font-mono font-bold">{r.consumed_qty}</td>
                    <td className="px-4 py-2 text-[11px] text-zinc-400">{r.first_date} → {r.last_date}</td>
                    <td className="px-4 py-2 text-[11px]">{r.txn_types?.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
